import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  ChartBar,
  Users,
  Folder,
  UserPlus,
  Shield,
  Gear,
  SpeakerHigh,
  TextAa,
  Database,
  ChartLineUp,
  Robot,
  ChatCenteredDots,
  Key,
  WarningOctagon,
  Crown,
  ShieldCheck,
  FileText,
  Palette,
  Link,
  Tag,
  MagnifyingGlass,
  Wrench,
  ArrowClockwise,
  Phone
} from '@phosphor-icons/react';

// Custom Settings Components
import MaintenanceMode from '../components/settings/MaintenanceMode';
import AdminProfileUpdate from '../components/AdminProfileUpdate';
import AppSettingsManagement from '../components/AppSettingsManagement';
import HandlerManagement from '../components/HandlerManagement';
import BackupRestore from '../components/BackupRestore';
import DisplayNamesSettings from '../components/DisplayNamesSettings';
import RegisteredDevicesSection from '../components/RegisteredDevicesSection';
import StatusManagement from '../components/StatusManagement';
// Newly Created Settings Components
import RingtoneSounds from '../components/settings/RingtoneSounds';
import HandlerStatus from '../components/settings/HandlerStatus';
import SupportBot from '../components/settings/SupportBot';
import BotAutoMessages from '../components/settings/BotAutoMessages';
import PasswordRequests from '../components/settings/PasswordRequests';
import SuspiciousCustomers from '../components/settings/SuspiciousCustomers';
import PremiumCustomers from '../components/settings/PremiumCustomers';
import Permissions from '../components/settings/Permissions';
import Reports from '../components/settings/Reports';
import Appearance from '../components/settings/Appearance';
import ManageLinks from '../components/settings/ManageLinks';
import MBManagement from '../components/settings/MBManagement';
import BlockedClientsSection from '../components/BlockedClientsSection';
import AnalyticsHub from '../components/settings/AnalyticsHub';
import RevertRequests from '../components/settings/RevertRequests';
import CreateClient from '../components/settings/CreateClient';
import ClientsDirectory from '../components/settings/ClientsDirectory';
import LoginLogs from '../components/settings/LoginLogs';
import ServiceTimelines from '../components/settings/ServiceTimelines';
import CallingAgentReports from '../components/settings/CallingAgentReports';
import CallingMsgSettings from '../components/settings/CallingMsgSettings';
import AdminAccessCode from '../components/AdminAccessCode';
import InquiryAutoMessages from '../components/settings/InquiryAutoMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SETTINGS_CARDS = [
  { id: 'profile', title: 'Update Profile', desc: 'Mobile · Email · Password', icon: User, color: 'text-blue-500', bg: 'bg-blue-50' },
  { id: 'calling_msg', title: 'Calling Msg', desc: 'Preset call outcome chips · Quick response templates', icon: Phone, color: 'text-red-500', bg: 'bg-red-50' },
  { id: 'analytics', title: 'Analytics', desc: 'Bot · Activity · IP · Blocked', icon: ChartBar, color: 'text-red-500', bg: 'bg-red-50' },
  { id: 'handlers', title: 'Handlers', desc: 'Create · Manage · MB assign', icon: Users, color: 'text-orange-500', bg: 'bg-orange-50' },
  { id: 'calling_agent_reports', title: 'Calling Agent Reports', desc: 'Call/WhatsApp activity · sent reports', icon: Phone, color: 'text-pink-500', bg: 'bg-pink-50' },
  { id: 'directory', title: 'Clients Directory', desc: 'MB-wise · WhatsApp Broadcast', icon: Folder, color: 'text-cyan-500', bg: 'bg-cyan-50' },
  { id: 'create_client', title: 'Create Client', desc: 'Add new customer account', icon: UserPlus, color: 'text-green-500', bg: 'bg-green-50' },
  { id: '2fa', title: '2FA Devices', desc: 'Trusted devices', icon: Shield, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  { id: 'access_code', title: 'Admin Access Code', desc: 'Generate a 12-hour login code', icon: Key, color: 'text-purple-600', bg: 'bg-purple-50' },
  { id: 'app_settings', title: 'App Settings', desc: 'Logos · Links · Global prefs', icon: Gear, color: 'text-blue-600', bg: 'bg-blue-50' },
  { id: 'ringtone', title: 'Ringtone & Sounds', desc: 'Message tone · Call ringtone', icon: SpeakerHigh, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  { id: 'display_names', title: 'Display Names', desc: 'DWS branding', icon: TextAa, color: 'text-red-500', bg: 'bg-red-50' },
  { id: 'backup', title: 'Backup & Restore', desc: 'Data safety', icon: Database, color: 'text-purple-500', bg: 'bg-purple-50' },
  { id: 'handler_status', title: 'Handler Status', desc: 'Handler activity', icon: ChartLineUp, color: 'text-green-600', bg: 'bg-green-50' },
  { id: 'inquiry_auto_messages', title: 'Inquiry Auto Messages', desc: 'Text · Audio · Image · Ordered', icon: ChatCenteredDots, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  { id: 'support_bot', title: 'Support Bot', desc: 'Quick-reply options', icon: Robot, color: 'text-purple-600', bg: 'bg-purple-50' },
  { id: 'bot_messages', title: 'Bot & Auto-Messages', desc: 'On/Off · Templates · Links', icon: ChatCenteredDots, color: 'text-blue-500', bg: 'bg-blue-50' },
  { id: 'password_requests', title: 'Password Requests', desc: 'Handler reset approvals', icon: Key, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  { id: 'revert_requests', title: 'Revert Requests', desc: 'Withdrawal reversal approvals', icon: ArrowClockwise, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { id: 'suspicious', title: 'Suspicious Customers', desc: 'Red-flag list · action needed', icon: WarningOctagon, color: 'text-red-600', bg: 'bg-red-50' },
  { id: 'premium', title: 'Premium Customers', desc: 'Green-flag list · VIP', icon: Crown, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  { id: 'permissions', title: 'Permissions', desc: 'Approval requests hub', icon: ShieldCheck, color: 'text-green-500', bg: 'bg-green-50' },
  { id: 'reports', title: 'Deposit / Withdraw Reports', desc: 'Excel · PDF · MB-wise', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  { id: 'appearance', title: 'Appearance', desc: 'Theme · Font', icon: Palette, color: 'text-orange-500', bg: 'bg-orange-50' },
  { id: 'links', title: 'Manage Links', desc: 'Customer-facing links', icon: Link, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  { id: 'mb_management', title: 'MB Management', desc: 'Create · Hide · Unhide MBs', icon: Tag, color: 'text-amber-500', bg: 'bg-amber-50' },
  { id: 'maintenance', title: 'Maintenance Mode', desc: 'Pause app for users', icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-50' },
  { id: 'login_logs', title: 'Login Logs', desc: 'Handler login IP · Location · Time', icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'service_timelines', title: 'Service Timelines', desc: 'Deposit · Withdraw · Support requests', icon: ChartBar, color: 'text-blue-600', bg: 'bg-blue-50' }
];
export default function AdminSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(() => {
    const sec = searchParams.get('section');
    if (sec === 'calling-agent-reports' || sec === 'calling_agent_reports') return 'calling_agent_reports';
    return null;
  });
  const [selectedHandlerId, setSelectedHandlerId] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const searchInputRef = useRef(null);

  // Keyboard shortcut listener (Ctrl+K / ⌘K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch admin settings on mount to get dynamic email
  useEffect(() => {
    const fetchAdminEmail = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setAdminEmail(response.data.admin_email || 'admin@dws.com');
      } catch (err) {
        console.error('Failed to fetch admin settings:', err);
      }
    };
    fetchAdminEmail();
  }, []);

  const filteredCards = SETTINGS_CARDS.filter((card) => {
    if (card.id === 'support_bot') return false;
    const matchesSearch =
      card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.desc.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    if (user?.role === 'super_admin') return true;

    // Filter by handler settings_powers if user is a handler
    const powers = user?.settings_powers;
    if (!powers) return true;

    if (card.id === 'directory' && powers.client_directory === false) return false;
    if (card.id === 'create_client' && powers.create_client === false) return false;
    if (card.id === 'appearance' && powers.appearance === false) return false;
    if (card.id === 'handler_status' && powers.status === false) return false;
    if (card.id === 'reports' && powers.reports === false) return false;
    if (card.id === 'service_timelines' && powers.service_timelines === false) return false;
    if (card.id === 'handlers' && powers.enquiry_handler_mb === false) return false;

    return true;
  });

  const handleCardClick = (id) => {
    if (id === 'create_client') navigate('/admin/create-client');
    else {
      setActiveSection(id);
      if (id === 'access_code') {
        navigate('/admin-settings?section=access_code', { replace: true });
      }
    }
  };

  const handleAccessCodeBack = () => {
    setActiveSection(null);
    navigate('/admin-settings', { replace: true });
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile': return <AdminProfileUpdate adminId={user?.id} currentPhone={user?.phone} currentEmail={adminEmail} />;
      case 'app_settings': return <AppSettingsManagement />;
      case 'handlers': return <HandlerManagement onViewLogs={(handlerId) => {
        setSelectedHandlerId(handlerId);
        setActiveSection('login_logs');
      }} />;
      case '2fa': return <RegisteredDevicesSection />;
      case 'access_code': return <AdminAccessCode onBack={handleAccessCodeBack} />;
      case 'display_names': return <DisplayNamesSettings />;
      case 'backup': return <BackupRestore />;
      case 'ringtone': return <RingtoneSounds />;
      case 'handler_status': return <StatusManagement user={user} />;
      case 'bot_messages': return <BotAutoMessages onBack={() => setActiveSection(null)} />;
      case 'inquiry_auto_messages': return <InquiryAutoMessages onBack={() => setActiveSection(null)} />;
      case 'password_requests': return <PasswordRequests onBack={() => setActiveSection(null)} />;
      case 'appearance': return <Appearance />;
      case 'suspicious': return <SuspiciousCustomers />;
      case 'premium': return <PremiumCustomers />;
      case 'permissions': return <Permissions />;
      case 'support_bot': return <SupportBot />;
      case 'reports': return <Reports onClose={() => setActiveSection(null)} />;// 4. Reports Export
      case 'links': return <ManageLinks />;
      case 'mb_management': return <MBManagement onBack={() => setActiveSection(null)} />;
      case 'analytics': return <AnalyticsHub onSubSectionClick={(id) => setActiveSection(id)} />;
      case 'blocked_clients': return <BlockedClientsSection userId={user?.id} userRole={user?.role} />;
      case 'revert_requests': return <RevertRequests onBack={() => setActiveSection(null)} />;
      case 'create_client': return <CreateClient />;
      case 'login_logs': return <LoginLogs initialHandlerId={selectedHandlerId} onBack={() => {
        setSelectedHandlerId(null);
        setActiveSection('handlers');
      }} />;
      case 'maintenance': return <MaintenanceMode onBack={() => setActiveSection(null)} />;
      case 'service_timelines': return <ServiceTimelines onBack={() => setActiveSection(null)} />;
      case 'calling_msg': return <CallingMsgSettings />;

      default: return null;
    }
  };

  if (activeSection === 'password_requests') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] overflow-hidden">
        <PasswordRequests onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'revert_requests') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] overflow-hidden">
        <RevertRequests onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'maintenance') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] overflow-hidden">
        <MaintenanceMode onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'mb_management') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] overflow-hidden">
        <MBManagement onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'service_timelines') {
    return (
      <div className="h-screen w-screen bg-wa-bg fixed inset-0 z-[100] overflow-hidden">
        <ServiceTimelines onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'reports') {
    return (
      <div className="h-screen w-screen bg-white fixed inset-0 z-[100] p-1.5 md:p-3 overflow-hidden">
        <Reports onClose={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'directory') {
    return (
      <div className="h-screen w-screen bg-white fixed inset-0 z-[100] overflow-hidden">
        <ClientsDirectory onClose={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'login_logs') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] p-2 md:p-4 overflow-y-auto">
        <LoginLogs initialHandlerId={selectedHandlerId} onBack={() => {
          setSelectedHandlerId(null);
          setActiveSection(null);
        }} />
      </div>
    );
  }

  if (activeSection === 'calling_agent_reports') {
    return (
      <div className="h-screen w-screen bg-slate-100 fixed inset-0 z-[100] overflow-hidden">
        <CallingAgentReports onBack={() => setActiveSection(null)} />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#25D366] text-white p-4 flex-shrink-0 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => activeSection ? setActiveSection(null) : navigate('/admin-dashboard')}
            className="p-2 hover:bg-green-600 rounded-full transition-colors"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Settings</h1>
            <p className="text-sm text-green-100">{activeSection ? 'Configure Section' : 'Admin Control Center'}</p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {activeSection ? (
            /* Sub-page view */
            <div>
              <button
                onClick={() => setActiveSection(null)}
                className="mb-4 text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1"
              >
                <ArrowLeft size={16} weight="bold" /> Back to Settings
              </button>
              {renderActiveSection()}
            </div>
          ) : (
            /* Settings Hub Grid */
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Admin Control Center</h2>
                  <p className="text-sm text-gray-500">Choose a section to open. Each card opens on its own page.</p>
                </div>
                
                {/* Search Bar */}
                <div className="relative w-full md:w-80">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search settings... (⌘K / Ctrl+K)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-12 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
                  />
                  <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 border rounded font-mono pointer-events-none">
                    ⌘K
                  </kbd>
                </div>
              </div>

              {/* Grid Layout */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <button
                      key={card.id}
                      onClick={() => handleCardClick(card.id)}
                      className="flex items-start gap-4 p-5 bg-white border border-gray-150 rounded-xl hover:shadow-md hover:border-gray-300 transition-all text-left duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500/20"
                    >
                      <div className={`p-3 rounded-lg ${card.bg} ${card.color} flex-shrink-0`}>
                        <Icon size={24} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 text-sm truncate">{card.title}</h3>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">{card.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
